"use client";

import { c as _c } from "react/compiler-runtime";
import { AlertTriangle, Check, ChevronsUpDown, Filter } from "lucide-react";
import * as React from "react";

// import { Badge } from "@/lib/components/badge";
import { Button } from "@/lib/components/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/lib/components/command";
// import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/lib/components/hover-card";
import { Label } from "@/lib/components/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/lib/components/popover";
import { Separator } from "@/lib/components/separator";
import { Switch } from "@/lib/components/switch";
import { getProviderForModel } from "@/lib/model-utils";
import { cn } from "@/lib/utils";
import { getProviderIcon } from "@llmgateway/shared/components";
import type { ModelDefinition, ProviderDefinition, ProviderModelMapping } from "@llmgateway/models";
interface ModelSelectorProps {
  models: ModelDefinition[];
  providers: ProviderDefinition[];
  value?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  rootOnly?: boolean;
  id?: string;
}
interface FilterState {
  providers: string[];
  capabilities: string[];
  priceRange: "free" | "low" | "medium" | "high" | "all";
}

// helper to extract simple capability labels from a mapping
function getMappingCapabilities(mapping?: ProviderModelMapping): string[] {
  if (!mapping) {
    return [];
  }
  const labels: string[] = [];
  if (mapping.streaming) {
    labels.push("Streaming");
  }
  if (mapping.vision) {
    labels.push("Vision");
  }
  if (mapping.tools) {
    labels.push("Tools");
  }
  if ((mapping as any).reasoning) {
    labels.push("Reasoning");
  }
  return labels;
}
export function ModelSelector(t0) {
  const $ = _c(166);
  const {
    models,
    providers,
    value,
    onValueChange,
    placeholder: t1,
    rootOnly,
    id
  } = t0;
  const placeholder = t1 === undefined ? "Select model..." : t1;
  const [open, setOpen] = React.useState(false);
  const [filterOpen, setFilterOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      providers: [],
      capabilities: [],
      priceRange: "all"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const [filters, setFilters] = React.useState(t2);
  const raw = value ?? "";
  let selectedProviderId;
  let selectedRegion;
  let t3;
  if ($[1] !== raw) {
    const [t4, selectedModelIdRaw] = raw.includes("/") ? raw.split("/") as [string, string] : ["", raw];
    selectedProviderId = t4;
    const lastColonIdx = selectedModelIdRaw.lastIndexOf(":");
    selectedRegion = lastColonIdx > -1 ? selectedModelIdRaw.slice(lastColonIdx + 1) : undefined;
    t3 = lastColonIdx > -1 ? selectedModelIdRaw.slice(0, lastColonIdx) : selectedModelIdRaw;
    $[1] = raw;
    $[2] = selectedProviderId;
    $[3] = selectedRegion;
    $[4] = t3;
  } else {
    selectedProviderId = $[2];
    selectedRegion = $[3];
    t3 = $[4];
  }
  const selectedModelId = t3;
  let T0;
  let T1;
  let T2;
  let availableCapabilities;
  let availableProviders;
  let clearFilters;
  let filteredEntries;
  let hasActiveFilters;
  let selectedEntryKey;
  let t10;
  let t11;
  let t12;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  let toggleCapabilityFilter;
  let toggleProviderFilter;
  let updateFilter;
  if ($[5] !== filters || $[6] !== id || $[7] !== models || $[8] !== open || $[9] !== placeholder || $[10] !== providers || $[11] !== rootOnly || $[12] !== searchQuery || $[13] !== selectedModelId || $[14] !== selectedProviderId || $[15] !== selectedRegion) {
    let t13;
    if ($[37] !== selectedModelId) {
      t13 = m => m.id === selectedModelId;
      $[37] = selectedModelId;
      $[38] = t13;
    } else {
      t13 = $[38];
    }
    const selectedModel = models.find(t13);
    let t14;
    if ($[39] !== selectedProviderId) {
      t14 = p => p.id === selectedProviderId;
      $[39] = selectedProviderId;
      $[40] = t14;
    } else {
      t14 = $[40];
    }
    const selectedProviderDef = providers.find(t14);
    const selectedMapping = selectedRegion ? selectedModel?.providers.find(p_0 => p_0.providerId === selectedProviderId && p_0.region === selectedRegion) : selectedModel?.providers.find(p_1 => p_1.providerId === selectedProviderId && !p_1.region);
    selectedEntryKey = selectedModel && selectedProviderId && selectedMapping ? `${selectedProviderId}-${selectedModel.id}-${selectedMapping.region ?? ""}` : "";
    let out;
    if ($[41] !== models || $[42] !== providers || $[43] !== rootOnly) {
      out = [];
      let t15;
      if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
        t15 = new Date();
        $[45] = t15;
      } else {
        t15 = $[45];
      }
      const now = t15;
      for (const m_0 of models) {
        if (m_0.id === "custom") {
          continue;
        }
        if (rootOnly) {
          const activeProviders = m_0.providers.filter(mp => !mp.deactivatedAt || new Date(mp.deactivatedAt) > now);
          const stableProviders = activeProviders.filter(_temp);
          const candidates = stableProviders.length > 0 ? stableProviders : activeProviders;
          const cheapest = candidates.sort(_temp2)[0];
          if (cheapest) {
            out.push({
              model: m_0,
              mapping: cheapest,
              provider: providers.find(p_2 => p_2.id === cheapest.providerId)
            });
          }
        } else {
          for (const mp_1 of m_0.providers) {
            const isDeactivated = mp_1.deactivatedAt && new Date(mp_1.deactivatedAt) <= now;
            if (!isDeactivated) {
              out.push({
                model: m_0,
                mapping: mp_1,
                provider: providers.find(p_3 => p_3.id === mp_1.providerId)
              });
            }
          }
        }
      }
      $[41] = models;
      $[42] = providers;
      $[43] = rootOnly;
      $[44] = out;
    } else {
      out = $[44];
    }
    const allEntries = out;
    let t15;
    if ($[46] !== allEntries) {
      t15 = new Set(allEntries.map(_temp3));
      $[46] = allEntries;
      $[47] = t15;
    } else {
      t15 = $[47];
    }
    const ids = t15;
    let t16;
    if ($[48] !== ids || $[49] !== providers) {
      let t17;
      if ($[51] !== ids) {
        t17 = p_4 => ids.has(p_4.id as any);
        $[51] = ids;
        $[52] = t17;
      } else {
        t17 = $[52];
      }
      t16 = providers.filter(t17);
      $[48] = ids;
      $[49] = providers;
      $[50] = t16;
    } else {
      t16 = $[50];
    }
    availableProviders = t16;
    let t17;
    if ($[53] !== allEntries) {
      const set = new Set();
      allEntries.forEach(e_0 => getMappingCapabilities(e_0.mapping).forEach(c => set.add(c)));
      t17 = Array.from(set).sort();
      $[53] = allEntries;
      $[54] = t17;
    } else {
      t17 = $[54];
    }
    availableCapabilities = t17;
    let list = allEntries;
    if (searchQuery) {
      const normalize = _temp4;
      let t18;
      if ($[55] !== list || $[56] !== searchQuery) {
        const q = normalize(searchQuery);
        t18 = list.filter(t19 => {
          const {
            model,
            provider
          } = t19;
          const candidates_0 = [model.name ?? "", model.family, model.id, provider?.name ?? "", ...(model.aliases ?? [])];
          return candidates_0.some(c_0 => normalize(c_0).includes(q));
        });
        $[55] = list;
        $[56] = searchQuery;
        $[57] = t18;
      } else {
        t18 = $[57];
      }
      list = t18;
    }
    if (filters.providers.length > 0) {
      let t18;
      if ($[58] !== filters.providers || $[59] !== list) {
        t18 = list.filter(e_1 => filters.providers.includes(e_1.mapping.providerId));
        $[58] = filters.providers;
        $[59] = list;
        $[60] = t18;
      } else {
        t18 = $[60];
      }
      list = t18;
    }
    if (filters.capabilities.length > 0) {
      let t18;
      if ($[61] !== filters.capabilities || $[62] !== list) {
        t18 = list.filter(e_2 => {
          const caps = getMappingCapabilities(e_2.mapping);
          return filters.capabilities.every(c_1 => caps.includes(c_1));
        });
        $[61] = filters.capabilities;
        $[62] = list;
        $[63] = t18;
      } else {
        t18 = $[63];
      }
      list = t18;
    }
    if (filters.priceRange !== "all") {
      let t18;
      if ($[64] !== filters.priceRange || $[65] !== list) {
        t18 = list.filter(e_3 => {
          const price = Number(e_3.mapping.inputPrice ?? "0");
          switch (filters.priceRange) {
            case "free":
              {
                return price === 0;
              }
            case "low":
              {
                return price > 0 && price <= 0.000001;
              }
            case "medium":
              {
                return price > 0.000001 && price <= 0.00001;
              }
            case "high":
              {
                return price > 0.00001;
              }
            default:
              {
                return true;
              }
          }
        });
        $[64] = filters.priceRange;
        $[65] = list;
        $[66] = t18;
      } else {
        t18 = $[66];
      }
      list = t18;
    }
    filteredEntries = list;
    let t18;
    if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = (key, value_0) => {
        setFilters(prev => ({
          ...prev,
          [key]: value_0
        }));
      };
      $[67] = t18;
    } else {
      t18 = $[67];
    }
    updateFilter = t18;
    let t19;
    if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
      t19 = providerId => {
        setFilters(prev_0 => ({
          ...prev_0,
          providers: prev_0.providers.includes(providerId) ? prev_0.providers.filter(id_0 => id_0 !== providerId) : [...prev_0.providers, providerId]
        }));
      };
      $[68] = t19;
    } else {
      t19 = $[68];
    }
    toggleProviderFilter = t19;
    let t20;
    if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
      t20 = capability => {
        setFilters(prev_1 => ({
          ...prev_1,
          capabilities: prev_1.capabilities.includes(capability) ? prev_1.capabilities.filter(cap => cap !== capability) : [...prev_1.capabilities, capability]
        }));
      };
      $[69] = t20;
    } else {
      t20 = $[69];
    }
    toggleCapabilityFilter = t20;
    let t21;
    if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
      t21 = () => {
        setFilters({
          providers: [],
          capabilities: [],
          priceRange: "all"
        });
      };
      $[70] = t21;
    } else {
      t21 = $[70];
    }
    clearFilters = t21;
    hasActiveFilters = filters.providers.length > 0 || filters.capabilities.length > 0 || filters.priceRange !== "all";
    T2 = Popover;
    t11 = open;
    t12 = setOpen;
    T1 = PopoverTrigger;
    t10 = true;
    T0 = Button;
    t4 = id;
    t5 = "outline";
    t6 = "combobox";
    t7 = open;
    t8 = "w-full justify-between h-12 px-4 bg-transparent";
    t9 = selectedModel ? <div className="flex items-center gap-3">{(() => {
        const iconId = rootOnly ? selectedModel.family : (selectedProviderDef ?? getProviderForModel(selectedModel, providers))?.id;
        const IconComp = iconId ? getProviderIcon(iconId as string) : null;
        const iconColor = rootOnly ? undefined : ((selectedProviderDef ?? getProviderForModel(selectedModel, providers)) as any)?.color;
        return IconComp ? <IconComp className="h-5 w-5" style={iconColor ? {
          color: iconColor
        } : undefined} /> : null;
      })()}<div className="flex flex-col items-start"><div className="flex items-center gap-1"><span className="font-medium max-w-40 truncate">{selectedModel.name}</span>{(() => {
            const mapping = selectedModel.providers.find(p_5 => p_5.providerId === selectedProviderId);
            const isDeprecated = mapping?.deprecatedAt && new Date(mapping.deprecatedAt) <= new Date();
            return isDeprecated ? <AlertTriangle className="h-3.5 w-3.5 text-yellow-600 dark:text-yellow-500" /> : null;
          })()}</div>{!rootOnly && <span className="text-xs text-muted-foreground">{(selectedProviderDef ?? getProviderForModel(selectedModel, providers))?.name}{(() => {
            const mapping_0 = selectedModel.providers.find(p_6 => p_6.providerId === selectedProviderId);
            return mapping_0?.region ? ` (${mapping_0.region})` : null;
          })()}</span>}</div></div> : placeholder;
    $[5] = filters;
    $[6] = id;
    $[7] = models;
    $[8] = open;
    $[9] = placeholder;
    $[10] = providers;
    $[11] = rootOnly;
    $[12] = searchQuery;
    $[13] = selectedModelId;
    $[14] = selectedProviderId;
    $[15] = selectedRegion;
    $[16] = T0;
    $[17] = T1;
    $[18] = T2;
    $[19] = availableCapabilities;
    $[20] = availableProviders;
    $[21] = clearFilters;
    $[22] = filteredEntries;
    $[23] = hasActiveFilters;
    $[24] = selectedEntryKey;
    $[25] = t10;
    $[26] = t11;
    $[27] = t12;
    $[28] = t4;
    $[29] = t5;
    $[30] = t6;
    $[31] = t7;
    $[32] = t8;
    $[33] = t9;
    $[34] = toggleCapabilityFilter;
    $[35] = toggleProviderFilter;
    $[36] = updateFilter;
  } else {
    T0 = $[16];
    T1 = $[17];
    T2 = $[18];
    availableCapabilities = $[19];
    availableProviders = $[20];
    clearFilters = $[21];
    filteredEntries = $[22];
    hasActiveFilters = $[23];
    selectedEntryKey = $[24];
    t10 = $[25];
    t11 = $[26];
    t12 = $[27];
    t4 = $[28];
    t5 = $[29];
    t6 = $[30];
    t7 = $[31];
    t8 = $[32];
    t9 = $[33];
    toggleCapabilityFilter = $[34];
    toggleProviderFilter = $[35];
    updateFilter = $[36];
  }
  let t13;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />;
    $[71] = t13;
  } else {
    t13 = $[71];
  }
  let t14;
  if ($[72] !== T0 || $[73] !== t4 || $[74] !== t5 || $[75] !== t6 || $[76] !== t7 || $[77] !== t8 || $[78] !== t9) {
    t14 = <T0 id={t4} variant={t5} role={t6} aria-expanded={t7} className={t8}>{t9}{t13}</T0>;
    $[72] = T0;
    $[73] = t4;
    $[74] = t5;
    $[75] = t6;
    $[76] = t7;
    $[77] = t8;
    $[78] = t9;
    $[79] = t14;
  } else {
    t14 = $[79];
  }
  let t15;
  if ($[80] !== T1 || $[81] !== t10 || $[82] !== t14) {
    t15 = <T1 asChild={t10}>{t14}</T1>;
    $[80] = T1;
    $[81] = t10;
    $[82] = t14;
    $[83] = t15;
  } else {
    t15 = $[83];
  }
  let t16;
  if ($[84] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      zIndex: 99999
    };
    $[84] = t16;
  } else {
    t16 = $[84];
  }
  let t17;
  if ($[85] !== searchQuery) {
    t17 = <CommandInput placeholder="Search models..." value={searchQuery} onValueChange={setSearchQuery} className="h-12 border-0" />;
    $[85] = searchQuery;
    $[86] = t17;
  } else {
    t17 = $[86];
  }
  const t18 = hasActiveFilters && "text-primary";
  let t19;
  if ($[87] !== t18) {
    t19 = cn("ml-2 h-8 w-8 p-0", t18);
    $[87] = t18;
    $[88] = t19;
  } else {
    t19 = $[88];
  }
  let t20;
  if ($[89] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <Filter className="h-4 w-4" />;
    $[89] = t20;
  } else {
    t20 = $[89];
  }
  let t21;
  if ($[90] !== t19) {
    t21 = <PopoverTrigger asChild={true}><Button variant="ghost" size="sm" className={t19}>{t20}</Button></PopoverTrigger>;
    $[90] = t19;
    $[91] = t21;
  } else {
    t21 = $[91];
  }
  let t22;
  if ($[92] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = {
      zIndex: 100000
    };
    $[92] = t22;
  } else {
    t22 = $[92];
  }
  let t23;
  if ($[93] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <h4 className="font-medium">Filters</h4>;
    $[93] = t23;
  } else {
    t23 = $[93];
  }
  let t24;
  if ($[94] !== clearFilters || $[95] !== hasActiveFilters) {
    t24 = hasActiveFilters && <Button variant="ghost" size="sm" onClick={clearFilters}>Clear all</Button>;
    $[94] = clearFilters;
    $[95] = hasActiveFilters;
    $[96] = t24;
  } else {
    t24 = $[96];
  }
  let t25;
  if ($[97] !== t24) {
    t25 = <div className="flex items-center justify-between">{t23}{t24}</div>;
    $[97] = t24;
    $[98] = t25;
  } else {
    t25 = $[98];
  }
  let t26;
  if ($[99] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <Label className="text-sm font-medium">Providers</Label>;
    $[99] = t26;
  } else {
    t26 = $[99];
  }
  let t27;
  if ($[100] !== availableProviders || $[101] !== filters.providers || $[102] !== toggleProviderFilter) {
    let t28;
    if ($[104] !== filters.providers || $[105] !== toggleProviderFilter) {
      t28 = (provider_0, index) => {
        const ProviderIcon = getProviderIcon(provider_0.id);
        return <div key={`${provider_0.id}-${index}`} className="flex items-center space-x-2"><Switch id={`provider-${provider_0.id}`} checked={filters.providers.includes(provider_0.id)} onCheckedChange={() => toggleProviderFilter(provider_0.id)} /><Label htmlFor={`provider-${provider_0.id}`} className="flex items-center gap-2 text-sm cursor-pointer">{ProviderIcon && <ProviderIcon className="h-3 w-3" style={{
              color: provider_0.color
            }} />}{provider_0.name}</Label></div>;
      };
      $[104] = filters.providers;
      $[105] = toggleProviderFilter;
      $[106] = t28;
    } else {
      t28 = $[106];
    }
    t27 = availableProviders.map(t28);
    $[100] = availableProviders;
    $[101] = filters.providers;
    $[102] = toggleProviderFilter;
    $[103] = t27;
  } else {
    t27 = $[103];
  }
  let t28;
  if ($[107] !== t27) {
    t28 = <div className="space-y-2">{t26}<div className="space-y-2 max-h-32 overflow-y-auto">{t27}</div></div>;
    $[107] = t27;
    $[108] = t28;
  } else {
    t28 = $[108];
  }
  let t29;
  if ($[109] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <Separator />;
    $[109] = t29;
  } else {
    t29 = $[109];
  }
  let t30;
  if ($[110] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <Label className="text-sm font-medium">Capabilities</Label>;
    $[110] = t30;
  } else {
    t30 = $[110];
  }
  let t31;
  if ($[111] !== availableCapabilities || $[112] !== filters.capabilities || $[113] !== toggleCapabilityFilter) {
    let t32;
    if ($[115] !== filters.capabilities || $[116] !== toggleCapabilityFilter) {
      t32 = capability_0 => <div key={capability_0} className="flex items-center space-x-2"><Switch id={`capability-${capability_0}`} checked={filters.capabilities.includes(capability_0)} onCheckedChange={() => toggleCapabilityFilter(capability_0)} /><Label htmlFor={`capability-${capability_0}`} className="text-sm cursor-pointer">{capability_0}</Label></div>;
      $[115] = filters.capabilities;
      $[116] = toggleCapabilityFilter;
      $[117] = t32;
    } else {
      t32 = $[117];
    }
    t31 = availableCapabilities.map(t32);
    $[111] = availableCapabilities;
    $[112] = filters.capabilities;
    $[113] = toggleCapabilityFilter;
    $[114] = t31;
  } else {
    t31 = $[114];
  }
  let t32;
  if ($[118] !== t31) {
    t32 = <div className="space-y-2">{t30}<div className="space-y-2 max-h-32 overflow-y-auto">{t31}</div></div>;
    $[118] = t31;
    $[119] = t32;
  } else {
    t32 = $[119];
  }
  let t33;
  if ($[120] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <Separator />;
    $[120] = t33;
  } else {
    t33 = $[120];
  }
  let t34;
  if ($[121] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <Label className="text-sm font-medium">Price Range</Label>;
    $[121] = t34;
  } else {
    t34 = $[121];
  }
  let t35;
  if ($[122] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = [{
      value: "all",
      label: "All models"
    }, {
      value: "free",
      label: "Free models"
    }, {
      value: "low",
      label: "Low cost (\u2264 $0.000001)"
    }, {
      value: "medium",
      label: "Medium cost (\u2264 $0.00001)"
    }, {
      value: "high",
      label: "High cost (> $0.00001)"
    }];
    $[122] = t35;
  } else {
    t35 = $[122];
  }
  let t36;
  if ($[123] !== filters.priceRange || $[124] !== updateFilter) {
    t36 = <div className="space-y-2">{t34}<div className="space-y-2">{t35.map(option => <div key={option.value} className="flex items-center space-x-2"><Switch id={`price-${option.value}`} checked={filters.priceRange === option.value} onCheckedChange={() => updateFilter("priceRange", option.value)} /><Label htmlFor={`price-${option.value}`} className="text-sm cursor-pointer">{option.label}</Label></div>)}</div></div>;
    $[123] = filters.priceRange;
    $[124] = updateFilter;
    $[125] = t36;
  } else {
    t36 = $[125];
  }
  let t37;
  if ($[126] !== t25 || $[127] !== t28 || $[128] !== t32 || $[129] !== t36) {
    t37 = <PopoverContent className="w-80" style={t22} side="right" align="start"><div className="space-y-4">{t25}{t28}{t29}{t32}{t33}{t36}</div></PopoverContent>;
    $[126] = t25;
    $[127] = t28;
    $[128] = t32;
    $[129] = t36;
    $[130] = t37;
  } else {
    t37 = $[130];
  }
  let t38;
  if ($[131] !== filterOpen || $[132] !== t21 || $[133] !== t37) {
    t38 = <Popover open={filterOpen} onOpenChange={setFilterOpen}>{t21}{t37}</Popover>;
    $[131] = filterOpen;
    $[132] = t21;
    $[133] = t37;
    $[134] = t38;
  } else {
    t38 = $[134];
  }
  let t39;
  if ($[135] !== t17 || $[136] !== t38) {
    t39 = <div className="flex items-center border-b px-3 w-full">{t17}{t38}</div>;
    $[135] = t17;
    $[136] = t38;
    $[137] = t39;
  } else {
    t39 = $[137];
  }
  let t40;
  if ($[138] !== clearFilters || $[139] !== hasActiveFilters) {
    t40 = hasActiveFilters && <Button variant="link" size="sm" onClick={clearFilters} className="mt-2">Clear filters to see all models</Button>;
    $[138] = clearFilters;
    $[139] = hasActiveFilters;
    $[140] = t40;
  } else {
    t40 = $[140];
  }
  let t41;
  if ($[141] !== t40) {
    t41 = <CommandEmpty>No models found.{t40}</CommandEmpty>;
    $[141] = t40;
    $[142] = t41;
  } else {
    t41 = $[142];
  }
  const t42 = filteredEntries.length !== 1 ? "s" : "";
  let t43;
  if ($[143] !== filteredEntries.length || $[144] !== t42) {
    t43 = <div className="px-2 py-1 text-xs text-muted-foreground">{filteredEntries.length} model{t42} found</div>;
    $[143] = filteredEntries.length;
    $[144] = t42;
    $[145] = t43;
  } else {
    t43 = $[145];
  }
  let t44;
  if ($[146] !== filteredEntries || $[147] !== onValueChange || $[148] !== rootOnly || $[149] !== selectedEntryKey) {
    t44 = filteredEntries.map(t45 => {
      const {
        model: model_0,
        mapping: mapping_1,
        provider: provider_1
      } = t45;
      const IconComp_0 = rootOnly ? getProviderIcon(model_0.family) : provider_1 ? getProviderIcon(provider_1.id) : null;
      const entryKey = `${mapping_1.providerId}-${model_0.id}-${mapping_1.region ?? ""}`;
      const isDeprecated_0 = mapping_1.deprecatedAt && new Date(mapping_1.deprecatedAt) <= new Date();
      return <CommandItem key={entryKey} value={entryKey} onSelect={() => {
        onValueChange?.(`${mapping_1.providerId}/${model_0.id}${mapping_1.region ? `:${mapping_1.region}` : ""}`);
        setOpen(false);
      }} className="p-3 cursor-pointer"><Check className={cn("h-4 w-4", entryKey === selectedEntryKey ? "opacity-100" : "opacity-0")} /><div className="flex items-center justify-between w-full"><div className="flex items-center gap-2">{IconComp_0 ? <IconComp_0 className="h-6 w-6 flex-shrink-0" /> : null}<div className="flex flex-col"><div className="flex items-center gap-1"><span className="font-medium">{model_0.name}</span>{isDeprecated_0 && <AlertTriangle className="h-3.5 w-3.5 text-yellow-600 dark:text-yellow-500" />}</div>{!rootOnly && <span className="text-xs text-muted-foreground">{provider_1?.name}{mapping_1.region && <span className="ml-1">({mapping_1.region})</span>}</span>}</div></div></div></CommandItem>;
    });
    $[146] = filteredEntries;
    $[147] = onValueChange;
    $[148] = rootOnly;
    $[149] = selectedEntryKey;
    $[150] = t44;
  } else {
    t44 = $[150];
  }
  let t45;
  if ($[151] !== t43 || $[152] !== t44) {
    t45 = <CommandGroup>{t43}{t44}</CommandGroup>;
    $[151] = t43;
    $[152] = t44;
    $[153] = t45;
  } else {
    t45 = $[153];
  }
  let t46;
  if ($[154] !== t41 || $[155] !== t45) {
    t46 = <CommandList className="max-h-[400px]">{t41}{t45}</CommandList>;
    $[154] = t41;
    $[155] = t45;
    $[156] = t46;
  } else {
    t46 = $[156];
  }
  let t47;
  if ($[157] !== t39 || $[158] !== t46) {
    t47 = <PopoverContent className="w-[600px] p-0" style={t16} sideOffset={4}><div className="flex"><div className="flex-1"><Command shouldFilter={false}>{t39}{t46}</Command></div></div></PopoverContent>;
    $[157] = t39;
    $[158] = t46;
    $[159] = t47;
  } else {
    t47 = $[159];
  }
  let t48;
  if ($[160] !== T2 || $[161] !== t11 || $[162] !== t12 || $[163] !== t15 || $[164] !== t47) {
    t48 = <T2 open={t11} onOpenChange={t12}>{t15}{t47}</T2>;
    $[160] = T2;
    $[161] = t11;
    $[162] = t12;
    $[163] = t15;
    $[164] = t47;
    $[165] = t48;
  } else {
    t48 = $[165];
  }
  return t48;
}
function _temp4(s) {
  return s.toLowerCase().replace(/[-_\s]+/g, "");
}
function _temp3(e) {
  return e.mapping.providerId;
}
function _temp2(a, b) {
  return Number(a.inputPrice ?? "0") - Number(b.inputPrice ?? "0");
}
function _temp(mp_0) {
  return !mp_0.stability || mp_0.stability === "stable" || mp_0.stability === "beta";
}